import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Stack,
  Switch,
  TextField,
} from '@mui/material';
import * as employeeApi from '../../lib/api/employee';

const DOC_TYPE_OPTIONS = [
  { value: 'ORDER_BOOK', label: '수주품의서' },
];

const AUTH_SCOPE_OPTIONS = [
  { value: 'FINAL', label: '전결 승인자' },
  { value: 'NOTIFIER', label: '보고 수신자' },
];

const buildInitialValues = (data) => ({
  docType: data?.docType || 'ORDER_BOOK',
  usrUuid: data?.usrUuid || '',
  authScope: data?.authScope || 'FINAL',
  minAmount: data?.minAmount ?? '',
  maxAmount: data?.maxAmount ?? '',
  activeYn: data?.activeYn || 'Y',
});

const normalizeAmount = (value) => {
  if (value === null || value === undefined || value === '') {
    return null;
  }
  return Number(value);
};

const ApprovalFinalAuthModal = ({
  open,
  data,
  onClose,
  onSave,
}) => {
  const [form, setForm] = useState(buildInitialValues(data));
  const [userOptions, setUserOptions] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setForm(buildInitialValues(data));
  }, [data, open]);

  useEffect(() => {
    if (!open) {
      return;
    }

    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        const res = await employeeApi.getEmployees({ useYn: 'Y' });
        setUserOptions(res.data.list || []);
      } catch (e) {
        setUserOptions([]);
      } finally {
        setLoadingUsers(false);
      }
    };

    fetchUsers();
  }, [open]);

  const handleChange = (field) => (e) => {
    const value = e.target.value;
    setForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === 'authScope' && value === 'NOTIFIER') {
        next.minAmount = '';
        next.maxAmount = '';
      }
      return next;
    });
  };

  const handleSubmit = () => {
    const minAmount = form.authScope === 'FINAL' ? normalizeAmount(form.minAmount) : null;
    const maxAmount = form.authScope === 'FINAL' ? normalizeAmount(form.maxAmount) : null;

    if (!form.usrUuid) {
      alert('권한자를 선택하세요.');
      return;
    }
    if (!form.docType) {
      alert('대상 문서를 선택하세요.');
      return;
    }
    if (Number.isNaN(minAmount) || Number.isNaN(maxAmount)) {
      alert('전결 금액을 숫자로 입력하세요.');
      return;
    }
    if (minAmount !== null && maxAmount !== null && minAmount > maxAmount) {
      alert('전결 금액 범위를 확인하세요.');
      return;
    }

    onSave({
      docType: form.docType,
      usrUuid: form.usrUuid,
      authScope: form.authScope,
      minAmount,
      maxAmount,
      activeYn: form.activeYn,
    });
  };

  const amountDisabled = form.authScope !== 'FINAL';

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{data ? '전결 권한 수정' : '전결 권한 등록'}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2} sx={{ mt: 0.5 }}>
          <TextField
            select
            label="권한자"
            value={form.usrUuid}
            onChange={handleChange('usrUuid')}
            fullWidth
            disabled={loadingUsers}
          >
            {userOptions.map((user) => (
              <MenuItem key={user.usrUuid} value={user.usrUuid}>
                {user.usrNm} {user.dptNm ? `(${user.dptNm})` : ''}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="대상 문서"
            value={form.docType}
            onChange={handleChange('docType')}
            fullWidth
          >
            {DOC_TYPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="권한 구분"
            value={form.authScope}
            onChange={handleChange('authScope')}
            fullWidth
          >
            {AUTH_SCOPE_OPTIONS.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>

          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            <TextField
              label="최소 금액"
              type="number"
              value={form.minAmount}
              onChange={handleChange('minAmount')}
              fullWidth
              disabled={amountDisabled}
            />
            <TextField
              label="최대 금액"
              type="number"
              value={form.maxAmount}
              onChange={handleChange('maxAmount')}
              fullWidth
              disabled={amountDisabled}
            />
          </Box>

          {data && (
            <FormControlLabel
              control={
                <Switch
                  checked={form.activeYn === 'Y'}
                  onChange={(e) => setForm((prev) => ({
                    ...prev,
                    activeYn: e.target.checked ? 'Y' : 'N',
                  }))}
                />
              }
              label="사용"
            />
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">닫기</Button>
        <Button onClick={handleSubmit} variant="contained">
          저장
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApprovalFinalAuthModal;
